import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AdminActionResponse, AdminExtractionTestRequest, AdminExtractionTestResponse, AdminMe, AdminStatus, AdminUser, AdminUserRoleUpdateResponse, AdminUsersResponse, LeaderboardBoard, MyContributionsResponse, MyContributionsTab, NotificationPreferences, Offer, OfferContributionProposal, OfferContributionsResponse, OfferSubmission, OfferSubmissionDocumentResponse, OfferSubmissionPayload, OfferSubmissionsResponse, PlanEntitlements, ProfileWizardAnswers, TopContributor, TopContributorsResponse, UsageStatus, UserContributionSummary, UserFile, UserProfile } from './models';
import { RuntimeConfigService } from './runtime-config.service';

interface CognitoAuthResponse {
  AuthenticationResult?: {
    IdToken?: string;
    AccessToken?: string;
    RefreshToken?: string;
  };
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  Session?: string;
  message?: string;
}

interface CognitoTokenEndpointResponse {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

export class ApiError extends Error {
  constructor(message: string, readonly messageKey = '', readonly errorCode = '') {
    super(message);
    this.name = 'ApiError';
  }
}

export interface AuthTokenSnapshot {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export interface OAuthAvatarImportStartResponse {
  status: 'REDIRECT' | 'SKIPPED' | string;
  authorizeUrl?: string;
  reason?: string;
}

interface CognitoSoftwareTokenResponse {
  SecretCode?: string;
  Session?: string;
}

interface CognitoGetUserResponse {
  PreferredMfaSetting?: string;
  UserMFASettingList?: string[];
}

export interface SmsPhoneVerificationRequestResponse {
  status: 'SMS_PHONE_VERIFICATION_REQUESTED' | string;
  smsPhoneNumberStatus?: string;
  smsVerificationRequestedAt?: string;
  retryAfterSeconds?: number;
}

export interface DiscordWebhookTestResponse {
  status: 'DISCORD_WEBHOOK_TEST_QUEUED' | string;
}

interface ApiListResponse<T> {
  items: T[];
}

export interface PaginatedOffersResponse {
  items: Offer[];
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

export type OfferSortKey = 'status' | 'title' | 'university' | 'country' | 'fitScore' | 'publishedAt' | 'deadline';
export type SortDirection = 'asc' | 'desc';

export interface ProfilePictureUploadRequest {
  uploadUrl: string;
  uploadMethod: 'PUT';
  headers?: Record<string, string>;
  expiresIn: number;
  avatarStatus: string;
}

export interface CreateUserFilePayload {
  type: UserFile['type'];
  title: string;
  status?: UserFile['status'];
  note?: string;
  originalFilename: string;
  contentType: string;
  contentBase64: string;
}

export interface ReplaceUserFileContentPayload {
  originalFilename: string;
  contentType: string;
  contentBase64: string;
}

export interface UserFileContentResponse {
  filename: string;
  contentType: string;
  contentBase64: string;
}

export interface PrivacyDeletionResponse {
  status: string;
  stop_processing: boolean;
  profile_anonymized: boolean;
  deleted_file_count: number;
  deleted_match_count: number;
  requested_at: string;
}

export interface PrivacyExportRequestResponse {
  status: string;
  requestId: string;
  requestedAt: string;
  message: string;
}

export interface CoverLetterRequestResponse {
  status: 'QUEUED' | string;
  requestId: string;
  offerId: string;
  message: string;
  quota?: Record<string, unknown>;
}

export interface CoverLetterArtifactResponse {
  status: 'READY' | 'PENDING' | string;
  offerId: string;
  coverLetter: string;
  metadata?: Record<string, unknown>;
  editedAt?: string;
}

export interface CheckoutSessionResponse {
  provider: string;
  sessionId: string;
  status: string;
  plan: string;
  billingPeriod: string;
  checkoutUrl: string;
}

export interface CustomerPortalSessionResponse {
  provider: string;
  sessionId: string;
  portalUrl: string;
}

export interface RegistrationRequest {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface RegistrationResponse {
  status: 'PENDING_EMAIL_VERIFICATION' | 'CONFIRMED' | string;
  userSub?: string;
  delivery?: Record<string, unknown>;
}

export interface AuthActionResponse {
  status: string;
  email?: string;
  delivery?: Record<string, unknown>;
}

export interface XOAuthProfileHint {
  firstName?: string;
  lastName?: string;
  email: string;
}

export interface ContactRequest {
  email: string;
  message: string;
  altcha: string;
}

export interface ContactResponse {
  status: 'SENT' | string;
  recipient: string;
}

export interface ContactCaptchaResponse {
  algorithm: string;
  challenge: string;
  salt: string;
  signature: string;
  maxnumber: number;
  expiresIn: number;
}

export interface SustainabilityMetricResponse {
  status: 'ok' | 'unavailable' | string;
  source: string;
  metricName: string;
  namespace: string;
  periodStart?: string;
  periodEnd?: string;
  periodLabel?: string;
  yearlyKgCO2e?: number | null;
  totalKgCO2e: number | null;
  latestDatapointAt: string | null;
  message?: string;
}

export interface SoftwareMfaSetupResponse {
  status: 'MFA_SETUP_STARTED';
  secretCode: string;
  session?: string;
}

export interface SoftwareMfaStatusResponse {
  enabled: boolean;
  preferred: boolean;
}

export interface SoftwareMfaChallenge {
  status: 'SOFTWARE_TOKEN_MFA_REQUIRED';
  username: string;
  session: string;
  destination?: string;
}

export type LoginResponse = { status: 'AUTHENTICATED' } | SoftwareMfaChallenge;

@Injectable({ providedIn: 'root' })
export class ApiService {
  private static readonly OPEN_DEADLINE_SENTINEL = '9999-12-31';
  private static readonly SAFE_FILE_TYPES: UserFile['type'][] = ['CV', 'Transcript of records', 'Cover letter', 'Recommendation letter', 'Other'];
  private static readonly SAFE_FILE_STATUSES: UserFile['status'][] = ['Draft', 'Review', 'Ready'];
  private static readonly SAFE_OFFER_STATUSES: Offer['status'][] = ['Seen', 'Liked', 'Applied', 'Interview', 'Archived'];
  private static readonly SAFE_SORT_KEYS: OfferSortKey[] = ['status', 'title', 'university', 'country', 'fitScore', 'publishedAt', 'deadline'];
  private static readonly SAFE_SORT_DIRECTIONS: SortDirection[] = ['asc', 'desc'];
  private static readonly SAFE_CONTRIBUTION_REACTIONS: Array<'like' | 'dislike'> = ['like', 'dislike'];
  private static readonly SAFE_CONTRIBUTION_FIELDS = ['deadline', 'funding', 'requirements', 'benefits', 'description', 'researchAreas', 'methods', 'applyUrl', 'sourceUrl', 'contact', 'notes'];
  private static readonly SAFE_LEADERBOARD_BOARDS: LeaderboardBoard[] = ['overall', 'liked', 'sent', 'reviewed'];
  private static readonly SAFE_CONTRIBUTION_TABS: MyContributionsTab[] = ['sent', 'liked'];
  private static readonly SAFE_BILLING_PERIODS = ['monthly', 'semiannual', 'annual'];
  private static readonly SAFE_PLAN_CODES = ['FREE', 'BASE', 'PRO'];
  private static readonly SAFE_ADMIN_SOURCES = ['all', 'euraxess', 'abg', 'inria_jobs', 'cnrs_emploi', 'academic_positions'];
  private static readonly SAFE_ADMIN_MATCHING_ACTIONS = ['daily_ingestion', 'plan_matching', 'sanitize_offers', 'ai_batch_controller', 'plan_notifications'];
  private static readonly SAFE_NOTIFICATION_DIGESTS = ['immediate', 'daily', 'weekly', 'none'];
  private static readonly SAFE_NOTIFICATION_KEYS: Array<keyof NotificationPreferences> = [
    'email', 'sms', 'whatsapp', 'discord', 'inApp', 'digest', 'immediateAlerts', 'marketing', 'product', 'security',
    'stopProcessing', 'smsCountryCode', 'smsPhoneNumber', 'whatsappPhoneNumber', 'discordUsername', 'discordMessagePrefix'
  ];
  private static readonly ALLOWED_PRESIGNED_UPLOAD_HEADER_NAMES = ['content-type', 'x-amz-acl', 'x-amz-server-side-encryption', 'x-amz-meta-original-filename'];
  private idToken = localStorage.getItem('phdcompass.idToken') ?? '';
  private accessToken = localStorage.getItem('phdcompass.accessToken') ?? '';
  private refreshToken = localStorage.getItem('phdcompass.refreshToken') ?? '';

  constructor(private http: HttpClient, private config: RuntimeConfigService) {}

  get hasToken(): boolean { return this.idToken.length > 0 || this.refreshToken.length > 0; }

  get authorizationHeader(): string { return this.idToken ? `Bearer ${this.idToken}` : ''; }

  get canRefreshSession(): boolean { return this.refreshToken.length > 0; }

  get runtimeConfig(): RuntimeConfigService { return this.config; }

  async login(username: string, password: string): Promise<LoginResponse> {
    if (!this.config.cognitoUserPoolClientId) {
      throw new Error('Cognito client id is not configured for this environment.');
    }

    const response = await firstValueFrom(this.http.post<CognitoAuthResponse>(
      this.config.cognitoEndpoint,
      {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: this.config.cognitoUserPoolClientId,
        AuthParameters: { USERNAME: username, PASSWORD: password }
      },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        })
      }
    ));

    if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
      if (!response.Session) throw new Error('Cognito requested MFA without returning a challenge session.');
      return {
        status: 'SOFTWARE_TOKEN_MFA_REQUIRED',
        username,
        session: response.Session,
        destination: response.ChallengeParameters?.['FRIENDLY_DEVICE_NAME']
      };
    }

    this.storeAuthResult(response, true);
    return { status: 'AUTHENTICATED' };
  }

  async completeSoftwareMfaLogin(username: string, code: string, session: string): Promise<LoginResponse> {
    if (!this.config.cognitoUserPoolClientId) {
      throw new Error('Cognito client id is not configured for this environment.');
    }

    const verificationCode = code.trim().replace(/\s+/g, '');
    if (!verificationCode) throw new Error('An authenticator code is required to complete login.');
    if (!session) throw new Error('The MFA challenge session has expired. Please sign in again.');

    const response = await firstValueFrom(this.http.post<CognitoAuthResponse>(
      this.config.cognitoEndpoint,
      {
        ChallengeName: 'SOFTWARE_TOKEN_MFA',
        ClientId: this.config.cognitoUserPoolClientId,
        ChallengeResponses: { USERNAME: username, SOFTWARE_TOKEN_MFA_CODE: verificationCode },
        Session: session
      },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge'
        })
      }
    ));

    this.storeAuthResult(response, true);
    return { status: 'AUTHENTICATED' };
  }

  async refreshSession(): Promise<void> {
    if (!this.refreshToken) throw new Error('No refresh token is available for this session.');
    if (!this.config.cognitoUserPoolClientId) {
      throw new Error('Cognito client id is not configured for this environment.');
    }

    const response = await firstValueFrom(this.http.post<CognitoAuthResponse>(
      this.config.cognitoEndpoint,
      {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: this.config.cognitoUserPoolClientId,
        AuthParameters: { REFRESH_TOKEN: this.refreshToken }
      },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        })
      }
    ));

    this.storeAuthResult(response, false);
  }

  async exchangeOAuthCodeForTokens(code: string, redirectUri: string, codeVerifier: string): Promise<void> {
    if (!this.config.cognitoHostedUiDomain) throw new Error('Cognito Hosted UI domain is not configured for this environment.');
    if (!this.config.cognitoUserPoolClientId) throw new Error('Cognito client id is not configured for this environment.');

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.config.cognitoUserPoolClientId,
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier
    });

    const response = await firstValueFrom(this.http.post<CognitoTokenEndpointResponse>(
      `${this.config.cognitoHostedUiDomain}/oauth2/token`,
      body.toString(),
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    ));

    this.storeOAuthTokens(response);
  }

  async exchangeXOAuthCodeForTokens(code: string, redirectUri: string, codeVerifier: string, profileHint?: XOAuthProfileHint | null): Promise<void> {
    const payload: Record<string, string> = {
      code: this.requiredTrimmed(code, 'Authorization code', 4096),
      redirectUri: this.requiredHttpsUrl(redirectUri, 'Redirect URI'),
      codeVerifier: this.requiredTrimmed(codeVerifier, 'Code verifier', 256)
    };
    if (profileHint) {
      payload['email'] = this.emailString(profileHint.email);
      const firstName = this.optionalString(profileHint.firstName)?.slice(0, 100);
      const lastName = this.optionalString(profileHint.lastName)?.slice(0, 100);
      if (firstName) payload['firstName'] = firstName;
      if (lastName) payload['lastName'] = lastName;
    }

    const response = await firstValueFrom(this.http.post<CognitoAuthResponse>(
      this.apiUrl('/auth/x/callback'),
      payload,
      this.optionalAuthOptions()
    ));

    this.storeAuthResult(response, true);
  }

  async register(payload: RegistrationRequest): Promise<RegistrationResponse> {
    return firstValueFrom(this.http.post<RegistrationResponse>(this.apiUrl('/auth/register'), this.registrationPayload(payload)));
  }

  async confirmRegistration(email: string, code: string): Promise<AuthActionResponse> {
    return firstValueFrom(this.http.post<AuthActionResponse>(this.apiUrl('/auth/confirm-registration'), { email: this.emailString(email), code: this.requiredTrimmed(code, 'Verification code', 256) }));
  }

  async requestPasswordReset(email: string): Promise<AuthActionResponse> {
    return firstValueFrom(this.http.post<AuthActionResponse>(this.apiUrl('/auth/password-reset/request'), { email: this.emailString(email) }));
  }

  async confirmPasswordReset(email: string, code: string, password: string): Promise<AuthActionResponse> {
    return firstValueFrom(this.http.post<AuthActionResponse>(this.apiUrl('/auth/password-reset/confirm'), { email: this.emailString(email), code: this.requiredTrimmed(code, 'Verification code', 256), password }));
  }

  async sendContactRequest(payload: ContactRequest): Promise<ContactResponse> {
    const body: Record<string, string> = {
      email: this.emailString(payload.email),
      message: this.requiredTrimmed(payload.message, 'Message', 5000),
      altcha: this.requiredTrimmed(payload.altcha, 'Captcha payload', 4096)
    };
    return firstValueFrom(this.http.post<ContactResponse>(this.apiUrl('/contact'), body));
  }

  async getContactCaptcha(): Promise<ContactCaptchaResponse> {
    return firstValueFrom(this.http.get<ContactCaptchaResponse>(this.apiUrl('/contact/captcha')));
  }

  get contactCaptchaUrl(): string {
    return this.absoluteApiUrl('/contact/captcha');
  }

  get offerSubmissionCaptchaUrl(): string {
    return this.absoluteApiUrl('/offer-submissions/captcha');
  }

  async submitOfferSubmission(payload: OfferSubmissionPayload): Promise<OfferSubmission> {
    const response = await firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl('/offer-submissions'), this.offerSubmissionPayload(payload)));
    return this.mapOfferSubmission(response);
  }

  async beginSoftwareMfaSetup(): Promise<SoftwareMfaSetupResponse> {
    if (!this.accessToken) throw new Error('A signed-in session is required to set up MFA.');
    const response = await this.cognitoRequest<CognitoSoftwareTokenResponse>('AssociateSoftwareToken', { AccessToken: this.accessToken });
    if (!response.SecretCode) throw new Error('Cognito did not return a software-token secret.');
    return { status: 'MFA_SETUP_STARTED', secretCode: response.SecretCode, session: response.Session };
  }

  async getSoftwareMfaStatus(): Promise<SoftwareMfaStatusResponse> {
    if (!this.accessToken) throw new Error('A signed-in session is required to inspect MFA status.');
    const response = await this.cognitoRequest<CognitoGetUserResponse>('GetUser', { AccessToken: this.accessToken });
    const enabled = (response.UserMFASettingList ?? []).includes('SOFTWARE_TOKEN_MFA');
    return { enabled, preferred: response.PreferredMfaSetting === 'SOFTWARE_TOKEN_MFA' };
  }

  async confirmSoftwareMfaSetup(code: string): Promise<AuthActionResponse> {
    if (!this.accessToken) throw new Error('A signed-in session is required to verify MFA.');
    const verificationCode = code.trim().replace(/\s+/g, '');
    if (!verificationCode) throw new Error('A verification code is required to verify MFA.');

    await this.cognitoRequest<Record<string, unknown>>('VerifySoftwareToken', {
      AccessToken: this.accessToken,
      UserCode: verificationCode,
      FriendlyDeviceName: 'PhD Compass authenticator app'
    });
    await this.cognitoRequest<Record<string, unknown>>('SetUserMFAPreference', {
      AccessToken: this.accessToken,
      SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true }
    });
    return { status: 'MFA_ENABLED' };
  }

  async requestEmailVerification(): Promise<AuthActionResponse> {
    if (!this.accessToken) throw new Error('A signed-in session is required to request email verification.');
    if (!this.accessTokenHasScope('aws.cognito.signin.user.admin')) return { status: 'EMAIL_VERIFICATION_SKIPPED' };
    await this.cognitoRequest<Record<string, unknown>>('GetUserAttributeVerificationCode', {
      AccessToken: this.accessToken,
      AttributeName: 'email'
    });
    return { status: 'EMAIL_VERIFICATION_REQUESTED' };
  }

  async disableSoftwareMfa(): Promise<AuthActionResponse> {
    if (!this.accessToken) throw new Error('A signed-in session is required to disable MFA.');
    await this.cognitoRequest<Record<string, unknown>>('SetUserMFAPreference', {
      AccessToken: this.accessToken,
      SoftwareTokenMfaSettings: { Enabled: false, PreferredMfa: false }
    });
    return { status: 'MFA_DISABLED' };
  }

  async requestSmsPhoneVerification(phoneNumber: string): Promise<SmsPhoneVerificationRequestResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.post<SmsPhoneVerificationRequestResponse>(
      this.apiUrl('/preferences/notifications/sms-verification/request'),
      { phoneNumber: this.requiredTrimmed(phoneNumber, 'Phone number', 64) },
      this.authOptions()
    )));
  }

  async confirmSmsPhoneVerification(code: string): Promise<AuthActionResponse> {
    const verificationCode = code.trim().replace(/\s+/g, '');
    if (!verificationCode) throw new Error('A verification code is required to verify the phone number.');
    return this.authenticatedRequest(firstValueFrom(this.http.post<AuthActionResponse>(
      this.apiUrl('/preferences/notifications/sms-verification/confirm'),
      { code: verificationCode },
      this.authOptions()
    )));
  }

  async testDiscordWebhook(webhookUrl: string, messagePrefix = ''): Promise<DiscordWebhookTestResponse> {
    const safeWebhookUrl = this.requiredHttpsUrl(webhookUrl, 'Discord webhook URL', ['discord.com', 'discordapp.com']);
    const safePrefix = this.trimmedString(messagePrefix, 256);
    return this.authenticatedRequest(firstValueFrom(this.http.post<DiscordWebhookTestResponse>(
      this.apiUrl('/preferences/notifications/discord-webhook/test'),
      { webhookUrl: safeWebhookUrl, messagePrefix: safePrefix, discordMessagePrefix: safePrefix },
      this.authOptions()
    )));
  }

  logout(): void {
    this.idToken = '';
    this.accessToken = '';
    this.refreshToken = '';
    localStorage.removeItem('phdcompass.idToken');
    localStorage.removeItem('phdcompass.accessToken');
    localStorage.removeItem('phdcompass.refreshToken');
  }

  authTokenSnapshot(): AuthTokenSnapshot {
    return { idToken: this.idToken, accessToken: this.accessToken, refreshToken: this.refreshToken };
  }

  restoreAuthTokenSnapshot(snapshot: AuthTokenSnapshot): void {
    this.idToken = snapshot.idToken;
    this.accessToken = snapshot.accessToken;
    this.refreshToken = snapshot.refreshToken;
    this.storeTokenValue('phdcompass.idToken', snapshot.idToken);
    this.storeTokenValue('phdcompass.accessToken', snapshot.accessToken);
    this.storeTokenValue('phdcompass.refreshToken', snapshot.refreshToken);
  }

  currentOrcidIdFromIdToken(): string {
    const claims = this.decodeJwtPayload(this.idToken);
    const identities = this.parseJsonArray(claims['identities']);
    for (const identity of identities) {
      const record = identity as Record<string, unknown>;
      const provider = String(record['providerName'] ?? record['providerType'] ?? '').toLowerCase();
      if (provider.includes('orcid')) {
        const orcidId = this.normalizeOrcidId(record['userId']);
        if (orcidId) return orcidId;
      }
    }
    for (const claimName of ['preferred_username', 'cognito:username', 'username', 'sub']) {
      const orcidId = this.normalizeOrcidId(claims[claimName]);
      if (orcidId) return orcidId;
    }
    return '';
  }

  storeOAuthTokens(response: CognitoTokenEndpointResponse): void {
    if (response.error) throw new Error(response.error_description || response.error);
    if (!response.id_token) throw new Error('Cognito did not return an ID token.');

    this.idToken = response.id_token;
    localStorage.setItem('phdcompass.idToken', response.id_token);

    if (response.access_token) {
      this.accessToken = response.access_token;
      localStorage.setItem('phdcompass.accessToken', response.access_token);
    }

    if (response.refresh_token) {
      this.refreshToken = response.refresh_token;
      localStorage.setItem('phdcompass.refreshToken', response.refresh_token);
    }
  }

  private storeAuthResult(response: CognitoAuthResponse, requireRefreshToken: boolean): void {
    const idToken = response.AuthenticationResult?.IdToken;
    const accessToken = response.AuthenticationResult?.AccessToken;
    const refreshToken = response.AuthenticationResult?.RefreshToken;
    if (!idToken) {
      if (response.ChallengeName) throw new Error(`Additional authentication challenge required: ${response.ChallengeName}`);
      throw new Error(response.message || 'Login failed.');
    }

    this.idToken = idToken;
    localStorage.setItem('phdcompass.idToken', idToken);

    if (accessToken) {
      this.accessToken = accessToken;
      localStorage.setItem('phdcompass.accessToken', accessToken);
    }

    if (refreshToken) {
      this.refreshToken = refreshToken;
      localStorage.setItem('phdcompass.refreshToken', refreshToken);
    } else if (requireRefreshToken) {
      this.refreshToken = '';
      localStorage.removeItem('phdcompass.refreshToken');
    }
  }

  async getProfile(): Promise<UserProfile> {
    const profile = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl('/profile'), this.authOptions())));
    return this.mapProfile(profile);
  }

  async updateProfile(profile: Partial<UserProfile>): Promise<UserProfile> {
    const updated = await this.authenticatedRequest(firstValueFrom(this.http.put<Record<string, unknown>>(this.apiUrl('/profile'), this.profilePayload(profile), this.authOptions())));
    return this.mapProfile(updated);
  }

  async requestProfilePictureUpload(file: File): Promise<ProfilePictureUploadRequest> {
    const contentType = this.safeUploadContentType(file.type);
    return this.authenticatedRequest(firstValueFrom(this.http.post<ProfilePictureUploadRequest>(this.apiUrl('/profile/picture'), {
      contentType,
      contentLength: file.size
    }, this.authOptions())));
  }

  async uploadProfilePicture(upload: ProfilePictureUploadRequest, file: File): Promise<void> {
    const uploadUrl = this.requiredHttpsUrl(upload.uploadUrl, 'Profile picture upload URL');
    const headers = new HttpHeaders(this.safePresignedUploadHeaders(upload.headers, file.type));
    await firstValueFrom(this.http.put(uploadUrl, file, { headers, responseType: 'text' }));
  }

  async deleteProfilePicture(): Promise<void> {
    await this.authenticatedRequest(firstValueFrom(this.http.delete<void>(this.apiUrl('/profile/picture'), this.authOptions())));
  }

  async startMicrosoftAvatarImport(redirectTo: string): Promise<OAuthAvatarImportStartResponse> {
    const params = new HttpParams().set('redirectTo', this.safeRelativeRedirect(redirectTo));
    return this.authenticatedRequest(firstValueFrom(this.http.get<OAuthAvatarImportStartResponse>(
      this.apiUrl('/auth/microsoft/photo/start'),
      { ...this.authOptions(), params }
    )));
  }

  async listFiles(): Promise<UserFile[]> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<ApiListResponse<Record<string, unknown>>>(this.apiUrl('/files'), this.authOptions())));
    return response.items.map(item => this.mapFile(item));
  }

  async listMatchedOffers(page = 1, limit = 10, sortKey?: OfferSortKey | null, sortDirection?: SortDirection | null): Promise<PaginatedOffersResponse> {
    const safePage = this.safeInteger(page, 1, 1, 10000);
    const safeLimit = this.safeInteger(limit, 10, 1, 100);
    let params = new HttpParams().set('page', String(safePage)).set('limit', String(safeLimit));
    if (sortKey && sortDirection && this.isOneOf(sortKey, ApiService.SAFE_SORT_KEYS) && this.isOneOf(sortDirection, ApiService.SAFE_SORT_DIRECTIONS)) {
      params = params.set('sortKey', sortKey).set('sortDirection', sortDirection);
    }
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<ApiListResponse<Record<string, unknown>> & Record<string, unknown>>(
      this.apiUrl('/offers/matches'),
      { ...this.authOptions(), params }
    )));
    const responseLimit = Number(response['limit'] ?? safeLimit) || safeLimit;
    const totalItems = Number(response['totalItems'] ?? response.items.length) || 0;
    return {
      items: response.items.map(item => this.mapOffer(item)),
      page: Number(response['page'] ?? safePage) || safePage,
      limit: responseLimit,
      totalItems,
      totalPages: Number(response['totalPages'] ?? Math.max(1, Math.ceil(totalItems / responseLimit))) || 1
    };
  }

  async getOffer(offerId: string): Promise<Offer> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl(`/offers/${encodeURIComponent(offerId)}`), this.authOptions())));
    return this.mapOffer(response);
  }

  async listOfferContributions(offerId: string): Promise<OfferContributionsResponse> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl(`/offers/${encodeURIComponent(offerId)}/contributions`), this.authOptions())));
    return this.mapOfferContributions(response, offerId);
  }

  async contributeOfferInfo(offerId: string, field: string, value: string, reaction: 'like' | 'dislike' = 'like'): Promise<OfferContributionsResponse> {
    const safeField = this.safeEnum(field, ApiService.SAFE_CONTRIBUTION_FIELDS, 'Contribution field');
    const safeReaction = this.safeEnum(reaction, ApiService.SAFE_CONTRIBUTION_REACTIONS, 'Contribution reaction');
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl(`/offers/${encodeURIComponent(offerId)}/contributions`), {
      field: safeField,
      value: this.requiredTrimmed(value, 'Contribution value', 4000),
      reaction: safeReaction
    }, this.authOptions())));
    return this.mapOfferContributions(response, offerId);
  }

  async listTopContributors(limit = 10, board: LeaderboardBoard = 'overall'): Promise<TopContributorsResponse> {
    const safeLimit = this.safeInteger(limit, 10, 1, 100);
    const safeBoard = this.safeEnum(board, ApiService.SAFE_LEADERBOARD_BOARDS, 'Leaderboard board');
    const params = new HttpParams().set('limit', String(safeLimit)).set('board', safeBoard);
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<ApiListResponse<Record<string, unknown>> & Record<string, unknown>>(
      this.apiUrl('/contributions/top'),
      { ...this.authOptions(), params }
    )));
    return {
      board: String(response['board'] ?? safeBoard) as LeaderboardBoard,
      items: response.items.map((item, index) => this.mapTopContributor(item, index + 1)),
      currentUser: response['currentUser'] || response['current_user'] ? this.mapTopContributor(response['currentUser'] ?? response['current_user'], 0) : null,
      limit: Number(response['limit'] ?? safeLimit) || safeLimit,
      totalContributors: Number(response['totalContributors'] ?? response['total_contributors'] ?? response.items.length) || 0,
      totalActiveContributions: Number(response['totalActiveContributions'] ?? response['total_active_contributions'] ?? 0) || 0,
      scoreFormula: String(response['scoreFormula'] ?? response['score_formula'] ?? '')
    };
  }

  async listMyContributions(page = 1, limit = 10, tab: MyContributionsTab = 'sent'): Promise<MyContributionsResponse> {
    const safePage = this.safeInteger(page, 1, 1, 10000);
    const safeLimit = this.safeInteger(limit, 10, 1, 100);
    const safeTab = this.safeEnum(tab, ApiService.SAFE_CONTRIBUTION_TABS, 'Contribution tab');
    const params = new HttpParams().set('page', String(safePage)).set('limit', String(safeLimit)).set('tab', safeTab);
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<ApiListResponse<Record<string, unknown>> & Record<string, unknown>>(
      this.apiUrl('/contributions/me'),
      { ...this.authOptions(), params }
    )));
    return {
      items: response.items.map(item => this.mapUserContributionSummary(item)),
      page: Number(response['page'] ?? safePage) || safePage,
      limit: Number(response['limit'] ?? safeLimit) || safeLimit,
      totalItems: Number(response['totalItems'] ?? response['total_items'] ?? response.items.length) || 0,
      totalPages: Number(response['totalPages'] ?? response['total_pages'] ?? 1) || 1
    };
  }

  async listPlans(): Promise<PlanEntitlements[]> {
    const response = await firstValueFrom(this.http.get<ApiListResponse<Record<string, unknown>>>(this.apiUrl('/plans'), this.optionalAuthOptions()));
    return response.items.map(item => this.mapPlanEntitlements(item));
  }

  async getSubscriptionEntitlements(): Promise<PlanEntitlements> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl('/subscription/entitlements'), this.authOptions())));
    return this.mapPlanEntitlements(response);
  }

  async createCheckoutSession(plan: string, billingPeriod: string): Promise<CheckoutSessionResponse> {
    const safePlan = this.safeEnum(plan, ApiService.SAFE_PLAN_CODES, 'Subscription plan');
    const safeBillingPeriod = this.safeEnum(billingPeriod, ApiService.SAFE_BILLING_PERIODS, 'Billing period');
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl('/subscription/checkout'), { plan: safePlan, billingPeriod: safeBillingPeriod }, this.authOptions())));
    return {
      provider: String(response['provider'] ?? 'stripe'),
      sessionId: String(response['sessionId'] ?? response['session_id'] ?? ''),
      status: String(response['status'] ?? 'checkout_pending'),
      plan: String(response['plan'] ?? safePlan),
      billingPeriod: String(response['billingPeriod'] ?? response['billing_period'] ?? safeBillingPeriod),
      checkoutUrl: String(response['checkoutUrl'] ?? response['checkout_url'] ?? '')
    };
  }

  async createCustomerPortalSession(): Promise<CustomerPortalSessionResponse> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl('/subscription/portal'), {}, this.authOptions())));
    return {
      provider: String(response['provider'] ?? 'stripe'),
      sessionId: String(response['sessionId'] ?? response['session_id'] ?? ''),
      portalUrl: String(response['portalUrl'] ?? response['portal_url'] ?? '')
    };
  }

  async getNotificationPreferences(): Promise<NotificationPreferences> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl('/preferences/notifications'), this.authOptions())));
    return this.mapNotificationPreferences(response);
  }

  async updateNotificationPreferences(preferences: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.put<Record<string, unknown>>(this.apiUrl('/preferences/notifications'), this.notificationPreferencesPayload(preferences), this.authOptions())));
    return this.mapNotificationPreferences(response);
  }

  async unsubscribeNotifications(reason = 'settings_unsubscribe'): Promise<NotificationPreferences> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl('/preferences/notifications/unsubscribe'), { reason: this.requiredTrimmed(reason, 'Reason', 128) }, this.authOptions())));
    return this.mapNotificationPreferences(response);
  }

  async objectToProcessing(reason = 'settings_privacy_objection'): Promise<NotificationPreferences> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl('/privacy/processing-objection'), { reason: this.requiredTrimmed(reason, 'Reason', 128) }, this.authOptions())));
    return this.mapNotificationPreferences(response);
  }

  async revokeProcessingObjection(reason = 'settings_revoke_privacy_objection'): Promise<NotificationPreferences> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl('/privacy/processing-objection/revoke'), { reason: this.requiredTrimmed(reason, 'Reason', 128) }, this.authOptions())));
    return this.mapNotificationPreferences(response);
  }

  async getUsage(feature: string): Promise<UsageStatus> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl(`/usage/${encodeURIComponent(feature)}`), this.authOptions())));
    return this.mapUsageStatus(response);
  }

  async updateOfferStatus(offerId: string, status: Offer['status']): Promise<Offer> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.patch<Record<string, unknown>>(this.apiUrl(`/offers/${encodeURIComponent(offerId)}/status`), { status: this.safeEnum(status, ApiService.SAFE_OFFER_STATUSES, 'Offer status') }, this.authOptions())));
    return this.mapOffer(response);
  }

  async saveOffer(offerId: string): Promise<Offer> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl(`/offers/${encodeURIComponent(offerId)}/save`), {}, this.authOptions())));
    return this.mapOffer(response);
  }

  async unsaveOffer(offerId: string): Promise<Offer> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.delete<Record<string, unknown>>(this.apiUrl(`/offers/${encodeURIComponent(offerId)}/save`), this.authOptions())));
    return this.mapOffer(response);
  }

  async requestCoverLetter(offerId: string): Promise<CoverLetterRequestResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.post<CoverLetterRequestResponse>(this.apiUrl(`/offers/${encodeURIComponent(offerId)}/cover-letter`), {}, this.authOptions())));
  }

  async getCoverLetter(offerId: string): Promise<CoverLetterArtifactResponse> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl(`/offers/${encodeURIComponent(offerId)}/cover-letter`), this.authOptions())));
    return this.mapCoverLetterArtifact(response, offerId);
  }

  async updateCoverLetter(offerId: string, coverLetter: string): Promise<CoverLetterArtifactResponse> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.put<Record<string, unknown>>(this.apiUrl(`/offers/${encodeURIComponent(offerId)}/cover-letter`), { coverLetter: this.requiredTrimmed(coverLetter, 'Cover letter', 25000) }, this.authOptions())));
    return this.mapCoverLetterArtifact(response, offerId);
  }

  private mapCoverLetterArtifact(response: Record<string, unknown>, offerId: string): CoverLetterArtifactResponse {
    return {
      status: String(response['status'] ?? 'PENDING'),
      offerId: String(response['offerId'] ?? response['offer_id'] ?? offerId),
      coverLetter: String(response['coverLetter'] ?? response['cover_letter'] ?? ''),
      metadata: response['metadata'] && typeof response['metadata'] === 'object' ? response['metadata'] as Record<string, unknown> : undefined,
      editedAt: response['editedAt'] || response['edited_at'] ? String(response['editedAt'] ?? response['edited_at']) : undefined
    };
  }

  async createFile(payload: CreateUserFilePayload): Promise<UserFile> {
    const created = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl('/files'), this.createFilePayload(payload), this.authOptions())));
    return this.mapFile(created);
  }

  async updateFile(file: UserFile): Promise<UserFile> {
    const updated = await this.authenticatedRequest(firstValueFrom(this.http.put<Record<string, unknown>>(this.apiUrl(`/files/${encodeURIComponent(file.id)}`), this.updateFilePayload(file), this.authOptions())));
    return this.mapFile(updated);
  }

  async replaceFileContent(file: UserFile, replacement: ReplaceUserFileContentPayload): Promise<UserFile> {
    const updated = await this.authenticatedRequest(firstValueFrom(this.http.put<Record<string, unknown>>(this.apiUrl(`/files/${encodeURIComponent(file.id)}`), this.createFilePayload({
      type: file.type,
      title: file.title,
      status: file.status,
      note: file.note,
      ...replacement
    }), this.authOptions())));
    return this.mapFile(updated);
  }

  async deleteFile(id: string): Promise<void> {
    await this.authenticatedRequest(firstValueFrom(this.http.delete<void>(this.apiUrl(`/files/${encodeURIComponent(id)}`), this.authOptions())));
  }

  async getFileContent(id: string): Promise<UserFileContentResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.get<UserFileContentResponse>(this.apiUrl(`/files/${encodeURIComponent(id)}/content`), this.authOptions())));
  }

  async exportPrivacyData(): Promise<PrivacyExportRequestResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.get<PrivacyExportRequestResponse>(this.apiUrl('/privacy/export'), this.authOptions())));
  }

  async deletePrivacyAccount(): Promise<PrivacyDeletionResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.delete<PrivacyDeletionResponse>(this.apiUrl('/privacy/account'), this.authOptions())));
  }

  async getAdminMe(): Promise<AdminMe> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl('/admin/me'), this.authOptions())));
    return {
      isAdmin: Boolean(response['isAdmin'] ?? response['is_admin'] ?? false),
      isModerator: Boolean(response['isModerator'] ?? response['is_moderator'] ?? response['isAdmin'] ?? response['is_admin'] ?? false),
      email: String(response['email'] ?? ''),
      userId: String(response['userId'] ?? response['user_id'] ?? ''),
      groups: this.optionalStringArray(response['groups']) ?? []
    };
  }

  async getAdminStatus(): Promise<AdminStatus> {
    return this.authenticatedRequest(firstValueFrom(this.http.get<AdminStatus>(this.apiUrl('/admin/status'), this.authOptions())));
  }

  async recordVisit(): Promise<void> {
    await firstValueFrom(this.http.post(this.apiUrl('/visits'), {}, this.optionalAuthOptions()));
  }

  async getSustainabilityMetric(): Promise<SustainabilityMetricResponse> {
    return firstValueFrom(this.http.get<SustainabilityMetricResponse>(this.apiUrl('/sustainability')));
  }

  async listAdminUsers(limit = 25, nextToken = ''): Promise<AdminUsersResponse> {
    let params = new HttpParams().set('limit', String(this.safeInteger(limit, 25, 1, 60)));
    if (nextToken) params = params.set('nextToken', this.trimmedString(nextToken, 2048));
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl('/admin/users'), { ...this.authOptions(), params })));
    return {
      items: Array.isArray(response['items']) ? response['items'].map(item => this.mapAdminUser(item as Record<string, unknown>)) : [],
      nextToken: String(response['nextToken'] ?? response['next_token'] ?? ''),
      limit: Number(response['limit'] ?? limit) || limit
    };
  }

  async updateAdminUserRole(username: string, group: 'ADMIN' | 'MODO', action: 'promote' | 'demote'): Promise<AdminUserRoleUpdateResponse> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(
      this.apiUrl('/admin/users/role'),
      { username: this.requiredTrimmed(username, 'Username', 256), group: this.safeEnum(group, ['ADMIN', 'MODO'], 'Admin group'), action: this.safeEnum(action, ['promote', 'demote'], 'Role action') },
      this.authOptions()
    )));
    return {
      status: String(response['status'] ?? ''),
      user: this.mapAdminUser((response['user'] as Record<string, unknown>) ?? {}),
      group: String(response['group'] ?? group),
      action: String(response['action'] ?? action)
    };
  }

  async forceAdminCrawl(source: string): Promise<AdminActionResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.post<AdminActionResponse>(this.apiUrl('/admin/crawl'), { source: this.safeEnum(source, ApiService.SAFE_ADMIN_SOURCES, 'Admin crawl source'), confirm: 'FORCE_CRAWL' }, this.authOptions())));
  }

  async runAdminMatching(action: string): Promise<AdminActionResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.post<AdminActionResponse>(this.apiUrl('/admin/matching'), { action: this.safeEnum(action, ApiService.SAFE_ADMIN_MATCHING_ACTIONS, 'Admin matching action'), confirm: 'RUN_MATCHING' }, this.authOptions())));
  }

  async testAdminApi(): Promise<AdminActionResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.post<AdminActionResponse>(this.apiUrl('/admin/test-api'), {}, this.authOptions())));
  }

  async sendAdminTestNotification(payload: { recipient: string; channel?: 'email' | 'whatsapp'; preferredLanguage?: string; name?: string }): Promise<AdminActionResponse> {
    const channel = payload.channel === 'whatsapp' ? 'whatsapp' : 'email';
    const recipient = channel === 'email' ? this.emailString(payload.recipient) : this.requiredTrimmed(payload.recipient, 'Recipient', 32);
    return this.authenticatedRequest(firstValueFrom(this.http.post<AdminActionResponse>(
      this.apiUrl('/admin/test-notification'),
      {
        recipient,
        channel,
        preferredLanguage: this.trimmedString(payload.preferredLanguage ?? '', 16),
        name: this.trimmedString(payload.name ?? '', 120)
      },
      this.authOptions()
    )));
  }

  async testAdminExtraction(payload: AdminExtractionTestRequest): Promise<AdminExtractionTestResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.post<AdminExtractionTestResponse>(this.apiUrl('/admin/test-extraction'), this.adminExtractionPayload(payload), this.authOptions())));
  }

  async extractExistingOffers(): Promise<AdminActionResponse> {
    return this.authenticatedRequest(firstValueFrom(this.http.post<AdminActionResponse>(this.apiUrl('/admin/extract-existing-offers'), { confirm: 'EXTRACT_EXISTING_OFFERS' }, this.authOptions())));
  }

  async listAdminOfferSubmissions(status = 'PENDING', limit = 50): Promise<OfferSubmissionsResponse> {
    const params = new HttpParams().set('status', this.safeEnum(status.toUpperCase(), ['PENDING', 'APPROVED', 'REJECTED'], 'Submission status')).set('limit', String(this.safeInteger(limit, 50, 1, 100)));
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl('/admin/offer-submissions'), { ...this.authOptions(), params })));
    return {
      items: Array.isArray(response['items']) ? response['items'].map(item => this.mapOfferSubmission(item as Record<string, unknown>)) : [],
      status: String(response['status'] ?? status),
      limit: Number(response['limit'] ?? limit) || limit
    };
  }

  async approveAdminOfferSubmission(submissionId: string, moderationNotes = ''): Promise<OfferSubmission> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl(`/admin/offer-submissions/${encodeURIComponent(submissionId)}/approve`), { confirm: 'APPROVE_OFFER_SUBMISSION', moderationNotes: this.trimmedString(moderationNotes, 2000) }, this.authOptions())));
    return this.mapOfferSubmission(response);
  }

  async rejectAdminOfferSubmission(submissionId: string, moderationNotes = ''): Promise<OfferSubmission> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.post<Record<string, unknown>>(this.apiUrl(`/admin/offer-submissions/${encodeURIComponent(submissionId)}/reject`), { confirm: 'REJECT_OFFER_SUBMISSION', moderationNotes: this.trimmedString(moderationNotes, 2000) }, this.authOptions())));
    return this.mapOfferSubmission(response);
  }

  async getAdminOfferSubmissionDocument(submissionId: string): Promise<OfferSubmissionDocumentResponse> {
    const response = await this.authenticatedRequest(firstValueFrom(this.http.get<Record<string, unknown>>(this.apiUrl(`/admin/offer-submissions/${encodeURIComponent(submissionId)}/document`), this.authOptions())));
    return {
      downloadUrl: String(response['downloadUrl'] ?? response['download_url'] ?? ''),
      filename: String(response['filename'] ?? 'offer.pdf'),
      contentType: String(response['contentType'] ?? response['content_type'] ?? 'application/pdf'),
      expiresIn: Number(response['expiresIn'] ?? response['expires_in'] ?? 900) || 900
    };
  }

  private offerSubmissionPayload(payload: OfferSubmissionPayload): Record<string, unknown> {
    const body: Record<string, unknown> = {
      contributorEmail: this.emailString(payload.contributorEmail),
      title: this.requiredTrimmed(payload.title, 'Offer title', 300),
      organization: this.requiredTrimmed(payload.organization, 'Organization', 300),
      country: this.requiredTrimmed(payload.country, 'Country', 120),
      location: this.requiredTrimmed(payload.location, 'Location', 300),
      deadline: this.requiredTrimmed(payload.deadline, 'Application deadline', 64),
      publishedAt: this.requiredTrimmed(payload.publishedAt, 'Published date', 64),
      sourceUrl: this.requiredTrimmed(payload.sourceUrl, 'Source URL', 2048),
      applyLink: this.requiredTrimmed(payload.applyLink, 'Apply link or email', 2048),
      researchAreas: this.requiredStringArray(payload.researchAreas, 'Research areas'),
      description: this.requiredTrimmed(payload.description, 'Description', 12000),
      requirements: this.requiredStringArray(payload.requirements, 'Requirements'),
      benefits: this.requiredStringArray(payload.benefits, 'Benefits / funding'),
      contact: this.requiredTrimmed(payload.contact, 'Contact or supervisor', 500),
      altcha: this.requiredTrimmed(payload.altcha, 'Captcha payload', 4096)
    };
    if (payload.pdf) body['pdf'] = {
      filename: this.safeFilename(payload.pdf.filename),
      contentType: this.safeFileContentType(payload.pdf.contentType),
      contentBase64: this.requiredBase64(payload.pdf.contentBase64, 'PDF content')
    };
    return body;
  }

  private requiredStringArray(value: string[], label: string): string[] {
    const items = (value || []).map(item => this.trimmedString(String(item ?? ''), 500)).filter(Boolean);
    if (!items.length) throw new Error(`${label} is required`);
    return items;
  }

  private registrationPayload(payload: RegistrationRequest): RegistrationRequest {
    return {
      username: this.requiredTrimmed(payload.username, 'Username', 30).toLowerCase(),
      firstName: this.requiredTrimmed(payload.firstName, 'First name', 100),
      lastName: this.requiredTrimmed(payload.lastName, 'Last name', 100),
      email: this.emailString(payload.email),
      password: payload.password
    };
  }

  private profilePayload(profile: Partial<UserProfile>): Partial<UserProfile> {
    const payload: Partial<UserProfile> = {};
    if (profile.firstName !== undefined) payload.firstName = this.requiredTrimmed(profile.firstName, 'First name', 100);
    if (profile.lastName !== undefined) payload.lastName = this.requiredTrimmed(profile.lastName, 'Last name', 100);
    if (profile.email !== undefined) payload.email = this.emailString(profile.email);
    if (profile.preferredLanguage !== undefined) payload.preferredLanguage = this.trimmedString(profile.preferredLanguage, 16);
    if (profile.avatarImportEnabled !== undefined) payload.avatarImportEnabled = Boolean(profile.avatarImportEnabled);
    if (profile.orcidId !== undefined) payload.orcidId = this.trimmedString(profile.orcidId, 32);
    if (profile.orcidUri !== undefined) payload.orcidUri = this.trimmedString(profile.orcidUri, 128);
    if (profile.profileSummary !== undefined) payload.profileSummary = this.trimmedString(profile.profileSummary, 5000);
    if (profile.profileWizard !== undefined) payload.profileWizard = this.profileWizardPayload(profile.profileWizard);
    return payload;
  }

  private profileWizardPayload(wizard: ProfileWizardAnswers): ProfileWizardAnswers {
    return {
      diplomas: this.trimmedString(wizard.diplomas, 2000),
      favoriteTopics: this.trimmedString(wizard.favoriteTopics, 2000),
      dislikedTasks: this.trimmedString(wizard.dislikedTasks, 2000),
      methods: this.trimmedString(wizard.methods, 2000),
      values: this.trimmedString(wizard.values, 2000),
      targetEnvironment: this.trimmedString(wizard.targetEnvironment, 2000),
      achievements: this.trimmedString(wizard.achievements, 2000),
      motivation: this.trimmedString(wizard.motivation, 2000),
      extraNotes: this.trimmedString(wizard.extraNotes, 2000)
    };
  }

  private notificationPreferencesPayload(preferences: Partial<NotificationPreferences>): Partial<NotificationPreferences> {
    const payload: Partial<NotificationPreferences> = {};
    for (const key of ApiService.SAFE_NOTIFICATION_KEYS) {
      if (preferences[key] === undefined) continue;
      const value = preferences[key];
      if (typeof value === 'boolean') (payload as Record<string, unknown>)[key] = value;
      else if (key === 'digest') payload.digest = this.safeEnum(String(value), ApiService.SAFE_NOTIFICATION_DIGESTS, 'Notification digest');
      else (payload as Record<string, unknown>)[key] = this.trimmedString(String(value ?? ''), key === 'discordMessagePrefix' ? 256 : 128);
    }
    return payload;
  }

  private createFilePayload(payload: CreateUserFilePayload): CreateUserFilePayload {
    return {
      type: this.safeEnum(payload.type, ApiService.SAFE_FILE_TYPES, 'File type'),
      title: this.requiredTrimmed(payload.title, 'File title', 200),
      status: payload.status === undefined ? undefined : this.safeEnum(payload.status, ApiService.SAFE_FILE_STATUSES, 'File status'),
      note: payload.note === undefined ? undefined : this.trimmedString(payload.note, 5000),
      originalFilename: this.safeFilename(payload.originalFilename),
      contentType: this.safeFileContentType(payload.contentType),
      contentBase64: this.requiredBase64(payload.contentBase64, 'File content')
    };
  }

  private updateFilePayload(file: UserFile): Partial<UserFile> {
    return {
      type: this.safeEnum(file.type, ApiService.SAFE_FILE_TYPES, 'File type'),
      title: this.requiredTrimmed(file.title, 'File title', 200),
      status: this.safeEnum(file.status, ApiService.SAFE_FILE_STATUSES, 'File status'),
      note: this.trimmedString(file.note, 5000),
      originalFilename: file.originalFilename ? this.safeFilename(file.originalFilename) : undefined,
      contentType: file.contentType ? this.safeFileContentType(file.contentType) : undefined
    };
  }


  private mapAdminUser(item: Record<string, unknown>): AdminUser {
    const groups = this.optionalStringArray(item['groups']) ?? [];
    const isAdmin = Boolean(item['isAdmin'] ?? item['is_admin'] ?? groups.includes('ADMIN'));
    const isModerator = Boolean(item['isModerator'] ?? item['is_moderator'] ?? (isAdmin || groups.includes('MODO')));
    return {
      username: String(item['username'] ?? ''),
      email: String(item['email'] ?? ''),
      firstName: String(item['firstName'] ?? item['first_name'] ?? ''),
      lastName: String(item['lastName'] ?? item['last_name'] ?? ''),
      enabled: Boolean(item['enabled'] ?? false),
      status: String(item['status'] ?? ''),
      createdAt: String(item['createdAt'] ?? item['created_at'] ?? ''),
      groups,
      isAdmin,
      isModerator
    };
  }

  private adminExtractionPayload(payload: AdminExtractionTestRequest): AdminExtractionTestRequest {
    const sourceUrl = this.trimmedString(payload.sourceUrl ?? '', 2048);
    return {
      title: this.requiredTrimmed(payload.title, 'Extraction title', 300),
      organization: this.trimmedString(payload.organization ?? '', 300) || undefined,
      location: this.trimmedString(payload.location ?? '', 300) || undefined,
      sourceUrl: sourceUrl ? this.requiredHttpUrl(sourceUrl, 'Extraction source URL') : undefined,
      description: this.requiredTrimmed(payload.description, 'Extraction description', 20000)
    };
  }

  private safePresignedUploadHeaders(headers: Record<string, string> | undefined, fallbackContentType: string): Record<string, string> {
    const safeHeaders: Record<string, string> = {};
    const source = headers && Object.keys(headers).length ? headers : { 'content-type': fallbackContentType };
    for (const [name, value] of Object.entries(source)) {
      const normalizedName = name.trim().toLowerCase();
      if (!ApiService.ALLOWED_PRESIGNED_UPLOAD_HEADER_NAMES.includes(normalizedName)) continue;
      const safeValue = this.headerValue(value, 256);
      if (safeValue) safeHeaders[normalizedName] = normalizedName === 'content-type' ? this.safeUploadContentType(safeValue) : safeValue;
    }
    if (!safeHeaders['content-type']) safeHeaders['content-type'] = this.safeUploadContentType(fallbackContentType);
    return safeHeaders;
  }

  private requiredHttpsUrl(value: string, label: string, allowedHosts?: string[]): string {
    const parsed = this.parseUrl(value, label);
    if (parsed.protocol !== 'https:') throw new Error(`${label} must be an HTTPS URL.`);
    if (allowedHosts && !allowedHosts.some(host => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`))) {
      throw new Error(`${label} is not issued by a trusted host.`);
    }
    return parsed.toString();
  }

  private requiredHttpUrl(value: string, label: string): string {
    const parsed = this.parseUrl(value, label);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') throw new Error(`${label} must be an HTTP(S) URL.`);
    return parsed.toString();
  }

  private parseUrl(value: string, label: string): URL {
    const text = this.requiredTrimmed(value, label, 2048);
    try { return new URL(text); }
    catch { throw new Error(`${label} is not a valid URL.`); }
  }

  private requiredBase64(value: string, label: string): string {
    const text = this.requiredTrimmed(value, label, 20 * 1024 * 1024);
    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(text) || text.length % 4 !== 0) throw new Error(`${label} must be base64 encoded.`);
    return text;
  }

  private safeFilename(value: string): string {
    const filename = this.requiredTrimmed(value, 'Filename', 255).replace(/[\\/\u0000-\u001f\u007f]+/g, '_');
    if (filename === '.' || filename === '..') throw new Error('Filename is not allowed.');
    return filename;
  }

  private safeUploadContentType(value: string): string {
    const contentType = this.safeFileContentType(value);
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(contentType)) throw new Error('Profile picture must be a passive image type.');
    return contentType;
  }

  private safeFileContentType(value: string): string {
    const contentType = this.requiredTrimmed(value, 'Content type', 128).toLowerCase();
    if (!/^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/.test(contentType)) throw new Error('Content type is invalid.');
    return contentType;
  }

  private emailString(value: string): string {
    const email = this.requiredTrimmed(value, 'Email', 320).toLowerCase();
    if (!/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email)) throw new Error('Email is invalid.');
    return email;
  }

  private headerValue(value: string, maxLength: number): string {
    const text = this.trimmedString(value, maxLength);
    if (/[\r\n]/.test(text)) throw new Error('Header value is invalid.');
    return text;
  }

  private requiredTrimmed(value: string, label: string, maxLength: number): string {
    const text = this.trimmedString(value, maxLength);
    if (!text) throw new Error(`${label} is required.`);
    return text;
  }

  private safeRelativeRedirect(value: string): string {
    const text = this.trimmedString(value || '/', 512);
    if (!text.startsWith('/') || text.startsWith('//') || text.includes('\\')) return '/';
    return text;
  }

  private trimmedString(value: string, maxLength: number): string {
    const text = String(value ?? '').trim();
    if (text.length > maxLength) throw new Error(`Input exceeds ${maxLength} characters.`);
    return text;
  }

  private safeEnum<T extends string>(value: string, allowed: readonly T[], label: string): T {
    const text = this.requiredTrimmed(value, label, 128);
    if (!this.isOneOf(text, allowed)) throw new Error(`${label} is not allowed.`);
    return text;
  }

  private isOneOf<T extends string>(value: string, allowed: readonly T[]): value is T {
    return (allowed as readonly string[]).includes(value);
  }

  private safeInteger(value: number, fallback: number, min: number, max: number): number {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return fallback;
    return Math.min(max, Math.max(min, Math.trunc(numberValue)));
  }

  private authOptions(): { headers: HttpHeaders } {
    return { headers: new HttpHeaders({ Authorization: this.authorizationHeader }) };
  }

  private optionalAuthOptions(): { headers?: HttpHeaders } {
    // Public endpoints must stay callable even when the browser has a stale/expired token.
    // Sending an invalid Authorization header to an API Gateway route configured as NONE can
    // still trigger confusing client-side/auth failures in some browser flows, so omit it.
    return {};
  }

  private apiUrl(path: string): string {
    return `${this.normalizedApiBaseUrl()}${path}`;
  }

  private absoluteApiUrl(path: string): string {
    const url = this.apiUrl(path);
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return new URL(url, window.location.origin).toString();
  }

  private normalizedApiBaseUrl(): string {
    const apiBaseUrl = this.config.apiBaseUrl || '/api';
    return apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  }

  private storeTokenValue(storageKey: string, value: string): void {
    if (value) localStorage.setItem(storageKey, value);
    else localStorage.removeItem(storageKey);
  }

  private decodeJwtPayload(token: string): Record<string, unknown> {
    const payload = token.split('.')[1];
    if (!payload) return {};
    try {
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      return JSON.parse(atob(padded)) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private accessTokenHasScope(requiredScope: string): boolean {
    const scopes = String(this.decodeJwtPayload(this.accessToken)['scope'] ?? '').split(/\s+/).filter(Boolean);
    return scopes.includes(requiredScope);
  }

  private parseJsonArray(value: unknown): unknown[] {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string') return [];
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private normalizeOrcidId(value: unknown): string {
    const rawValue = String(value ?? '').trim();
    const match = /\d{4}-\d{4}-\d{4}-\d{3}[0-9X]/.exec(rawValue);
    return match?.[0] ?? '';
  }

  private resolveApiLink(url: string): string {
    if (!url || /^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
    return this.apiUrl(url.startsWith('/') ? url : `/${url}`);
  }

  private async cognitoRequest<T>(action: string, payload: Record<string, unknown>): Promise<T> {
    return firstValueFrom(this.http.post<T>(
      this.config.cognitoEndpoint,
      payload,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`
        })
      }
    ));
  }

  private async authenticatedRequest<T>(request: Promise<T>): Promise<T> {
    try {
      return await request;
    } catch (error) {
      const apiError = this.apiError(error);
      if (apiError) throw apiError;
      throw error;
    }
  }

  private apiError(error: unknown): ApiError | null {
    if (!(error instanceof HttpErrorResponse)) return null;
    const body = error.error as unknown;
    const parsedError = this.parseApiErrorBody(body);
    return parsedError ? new ApiError(parsedError.message || parsedError.messageKey || error.message, parsedError.messageKey, parsedError.errorCode) : null;
  }

  private parseApiErrorBody(body: unknown): { message: string; messageKey: string; errorCode: string } | null {
    if (body && typeof body === 'object') {
      return this.apiErrorFromRecord(body as Record<string, unknown>);
    }
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as unknown;
        if (parsed && typeof parsed === 'object') {
          return this.apiErrorFromRecord(parsed as Record<string, unknown>);
        }
      } catch {
        const message = body.trim();
        if (message) return { message, messageKey: '', errorCode: '' };
      }
    }
    return null;
  }

  private apiErrorFromRecord(record: Record<string, unknown>): { message: string; messageKey: string; errorCode: string } | null {
    const messageKey = String(record['messageKey'] ?? record['message_key'] ?? '').trim();
    const message = String(record['message'] ?? record['error_description'] ?? '').trim();
    const errorCode = String(record['error'] ?? '').trim();
    return message || messageKey || errorCode ? { message, messageKey, errorCode } : null;
  }

  private mapProfile(profile: Record<string, unknown>): UserProfile {
    const firstName = String(profile['first_name'] ?? profile['firstName'] ?? '');
    const lastName = String(profile['last_name'] ?? profile['lastName'] ?? '');
    const rawProfileWizard = profile['profile_wizard'] ?? profile['profileWizard'];
    return {
      username: String(profile['username'] ?? profile['user_id'] ?? ''),
      firstName,
      lastName,
      email: String(profile['email'] ?? ''),
      emailVerified: this.optionalBoolean(profile['email_verified'] ?? profile['emailVerified']),
      preferredLanguage: this.optionalString(profile['preferred_language'] ?? profile['preferredLanguage']),
      planTier: String(profile['plan_tier'] ?? profile['planTier'] ?? 'starter'),
      avatar: this.resolveApiLink(this.normalizeAvatarUrl(profile['avatar'])),
      avatarStatus: String(profile['avatar_upload_status'] ?? profile['avatarStatus'] ?? ''),
      avatarImportEnabled: this.optionalBoolean(profile['avatar_import_enabled'] ?? profile['avatarImportEnabled']),
      avatarImportProvider: this.optionalString(profile['avatar_import_provider'] ?? profile['avatarImportProvider']),
      loginProvider: this.optionalString(profile['login_provider'] ?? profile['loginProvider'] ?? profile['avatar_import_provider'] ?? profile['avatarImportProvider']),
      oauthAvatarProvider: this.optionalString(profile['oauth_avatar_provider'] ?? profile['oauthAvatarProvider']),
      oauthAvatarFingerprint: this.optionalString(profile['oauth_avatar_fingerprint'] ?? profile['oauthAvatarFingerprint']),
      oauthAvatarFingerprintType: this.optionalString(profile['oauth_avatar_fingerprint_type'] ?? profile['oauthAvatarFingerprintType']),
      oauthAvatarCheckedAt: this.optionalString(profile['oauth_avatar_checked_at'] ?? profile['oauthAvatarCheckedAt']),
      oauthAvatarImportedAt: this.optionalString(profile['oauth_avatar_imported_at'] ?? profile['oauthAvatarImportedAt']),
      orcidId: this.optionalString(profile['orcid_id'] ?? profile['orcidId']),
      orcidUri: this.optionalString(profile['orcid_uri'] ?? profile['orcidUri']),
      orcidConnectedAt: this.optionalString(profile['orcid_connected_at'] ?? profile['orcidConnectedAt']),
      usernameUpdatedAt: this.optionalString(profile['username_updated_at'] ?? profile['usernameUpdatedAt']),
      profileSummary: String(profile['profile_summary'] ?? profile['profileSummary'] ?? ''),
      profileWizard: this.mapProfileWizard(rawProfileWizard)
    };
  }

  private mapProfileWizard(value: unknown): ProfileWizardAnswers | undefined {
    if (!value || typeof value !== 'object') return undefined;

    const wizard = value as Record<string, unknown>;
    return {
      diplomas: String(wizard['diplomas'] ?? ''),
      favoriteTopics: String(wizard['favoriteTopics'] ?? wizard['favorite_topics'] ?? ''),
      dislikedTasks: String(wizard['dislikedTasks'] ?? wizard['disliked_tasks'] ?? ''),
      methods: String(wizard['methods'] ?? ''),
      values: String(wizard['values'] ?? ''),
      targetEnvironment: String(wizard['targetEnvironment'] ?? wizard['target_environment'] ?? ''),
      achievements: String(wizard['achievements'] ?? ''),
      motivation: String(wizard['motivation'] ?? ''),
      extraNotes: String(wizard['extraNotes'] ?? wizard['extra_notes'] ?? '')
    };
  }

  private normalizeAvatarUrl(value: unknown): string {
    const avatar = String(value ?? '').trim();
    if (!avatar.startsWith('{')) return avatar;

    try {
      const parsed = JSON.parse(avatar) as unknown;
      if (!parsed || typeof parsed !== 'object') return avatar;
      const record = parsed as Record<string, unknown>;
      const data = record['data'];
      if (data && typeof data === 'object') {
        const url = String((data as Record<string, unknown>)['url'] ?? '').trim();
        if (url) return url;
      }
      const url = String(record['url'] ?? '').trim();
      return url || avatar;
    } catch {
      return avatar;
    }
  }

  private mapFile(file: Record<string, unknown>): UserFile {
    const id = String(file['file_id'] ?? file['id'] ?? '');
    return {
      id,
      file_id: id,
      type: String(file['type'] ?? 'Other') as UserFile['type'],
      title: String(file['title'] ?? ''),
      status: String(file['status'] ?? 'Draft') as UserFile['status'],
      note: String(file['note'] ?? ''),
      updatedAt: this.formatUpdatedAt(String(file['updated_at'] ?? file['updatedAt'] ?? '')),
      updated_at: this.formatUpdatedAt(String(file['updated_at'] ?? '')),
      originalFilename: String(file['original_filename'] ?? file['originalFilename'] ?? ''),
      contentType: String(file['content_type'] ?? file['contentType'] ?? ''),
      contentLength: Number(file['content_length'] ?? file['contentLength'] ?? 0),
      sha256: String(file['sha256'] ?? ''),
      url: String(file['url'] ?? ''),
      downloadUrl: String(file['download_url'] ?? file['downloadUrl'] ?? file['url'] ?? ''),
      previewUrl: String(file['preview_url'] ?? file['previewUrl'] ?? file['url'] ?? '')
    };
  }

  private mapOffer(offer: Record<string, unknown>): Offer {
    return {
      id: String(offer['id'] ?? offer['offer_id'] ?? ''),
      title: String(offer['title'] ?? offer['offer_title'] ?? 'Untitled offer'),
      university: this.mapOfferText(
        [offer['university'], offer['offer_organization'], offer['organization'], offer['company'], offer['laboratory']],
        'Unknown organization'
      ),
      country: this.mapOfferText(
        [offer['country'], offer['offer_country'], offer['offer_location'], offer['location']],
        'Unknown location'
      ),
      status: this.mapOfferStatus(offer['status'] ?? offer['offer_status']),
      fitScore: Number(offer['fitScore'] ?? offer['fit_score'] ?? offer['overall_score'] ?? 0),
      deadline: this.mapOfferDeadline(
        offer['deadline'],
        offer['offer_deadline'],
        offer['application_deadline'],
        offer['applicationDeadline'],
        offer['deadline_at'],
        offer['validThrough'],
        offer['expires_at']
      ),
      tier: this.mapOfferTier(offer['tier']),
      saved: Boolean(offer['saved'] ?? offer['is_saved'] ?? false),
      publishedAt: this.optionalString(offer['publishedAt'] ?? offer['published_at'] ?? offer['offer_published_at'] ?? offer['posted_at']),
      lastRefreshedAt: this.optionalString(offer['lastRefreshedAt'] ?? offer['last_refreshed_at'] ?? offer['offer_last_refreshed_at'] ?? offer['updated_at']),
      department: this.optionalString(offer['department'] ?? offer['offer_department']),
      researchAreas: this.optionalStringArray(offer['researchAreas'] ?? offer['research_areas'] ?? offer['offer_research_areas'] ?? offer['matched_keywords']) ?? ['unknown'],
      methods: this.optionalStringArray(offer['methods'] ?? offer['offer_methods']),
      requirements: this.optionalStringArray(offer['requirements'] ?? offer['offer_requirements']),
      benefits: this.optionalStringArray(offer['benefits'] ?? offer['offer_benefits']),
      description: this.optionalString(offer['description'] ?? offer['offer_description']),
      sourceUrl: this.optionalString(offer['sourceUrl'] ?? offer['source_url'] ?? offer['offer_source_url']),
      applyUrl: this.optionalString(offer['applyUrl'] ?? offer['apply_url'] ?? offer['offer_apply_url']),
      contributions: this.mapContributionWinners(offer['contributions']),
      scoreBreakdown: this.mapScoreBreakdown(offer['scoreBreakdown'] ?? offer['score_breakdown'])
    };
  }

  private mapPlanEntitlements(plan: Record<string, unknown>): PlanEntitlements {
    return {
      plan: String(plan['plan'] ?? 'FREE'),
      displayName: String(plan['displayName'] ?? plan['display_name'] ?? plan['plan'] ?? 'Free'),
      priceEurMonthly: Number(plan['priceEurMonthly'] ?? plan['price_eur_monthly'] ?? 0),
      introductoryPriceEurFirstMonth: this.optionalNumber(plan['introductoryPriceEurFirstMonth'] ?? plan['introductory_price_eur_first_month']),
      annualDiscountPercent: Number(plan['annualDiscountPercent'] ?? plan['annual_discount_percent'] ?? 0),
      adsEnabled: Boolean(plan['adsEnabled'] ?? plan['ads_enabled'] ?? false),
      maxStoredFiles: Number(plan['maxStoredFiles'] ?? plan['max_stored_files'] ?? 0),
      maxSavedSearchAlerts: Number(plan['maxSavedSearchAlerts'] ?? plan['max_saved_search_alerts'] ?? 0),
      monthlyDetailedFitReports: Number(plan['monthlyDetailedFitReports'] ?? plan['monthly_detailed_fit_reports'] ?? 0),
      monthlyCoverLetterGenerations: Number(plan['monthlyCoverLetterGenerations'] ?? plan['monthly_cover_letter_generations'] ?? 0),
      monthlyProfileParagraphGenerations: Number(plan['monthlyProfileParagraphGenerations'] ?? plan['monthly_profile_paragraph_generations'] ?? 0),
      monthlyOfferComparisons: Number(plan['monthlyOfferComparisons'] ?? plan['monthly_offer_comparisons'] ?? 0),
      basicFitScoreEnabled: Boolean(plan['basicFitScoreEnabled'] ?? plan['basic_fit_score_enabled'] ?? false),
      detailedFitBreakdownEnabled: Boolean(plan['detailedFitBreakdownEnabled'] ?? plan['detailed_fit_breakdown_enabled'] ?? false),
      notificationMode: String(plan['notificationMode'] ?? plan['notification_mode'] ?? ''),
      allowedDocumentTypesForGeneration: this.optionalStringArray(plan['allowedDocumentTypesForGeneration'] ?? plan['allowed_document_types_for_generation']) ?? [],
      maxDocumentsPerGeneration: Number(plan['maxDocumentsPerGeneration'] ?? plan['max_documents_per_generation'] ?? 0),
      profileWizardEnabled: Boolean(plan['profileWizardEnabled'] ?? plan['profile_wizard_enabled'] ?? false),
      applicationHistoryEnabled: Boolean(plan['applicationHistoryEnabled'] ?? plan['application_history_enabled'] ?? false),
      versionHistoryEnabled: Boolean(plan['versionHistoryEnabled'] ?? plan['version_history_enabled'] ?? false),
      exportEnabled: Boolean(plan['exportEnabled'] ?? plan['export_enabled'] ?? false),
      exportMode: this.optionalString(plan['exportMode'] ?? plan['export_mode']),
      offerContributionsEnabled: Boolean(plan['offerContributionsEnabled'] ?? plan['offer_contributions_enabled'] ?? false),
      billingManagementAvailable: Boolean(plan['billingManagementAvailable'] ?? plan['billing_management_available'] ?? false),
      billingPeriod: this.optionalString(plan['billingPeriod'] ?? plan['billing_period'])
    };
  }

  private mapOfferContributions(response: Record<string, unknown>, offerId: string): OfferContributionsResponse {
    const rawFields = response['fields'] && typeof response['fields'] === 'object' ? response['fields'] as Record<string, unknown> : {};
    const fields: Record<string, OfferContributionProposal[]> = {};
    Object.entries(rawFields).forEach(([field, proposals]) => {
      fields[field] = Array.isArray(proposals) ? proposals.map(item => this.mapContributionProposal(item)) : [];
    });
    return {
      offerId: String(response['offerId'] ?? response['offer_id'] ?? offerId),
      fields,
      winners: this.mapContributionWinners(response['winners']) ?? {}
    };
  }

  private mapContributionWinners(value: unknown): Record<string, OfferContributionProposal> | undefined {
    if (!value || typeof value !== 'object') return undefined;
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([field, proposal]) => [field, this.mapContributionProposal(proposal)]));
  }

  private mapContributionProposal(value: unknown): OfferContributionProposal {
    const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};
    return {
      field: String(record['field'] ?? ''),
      value: String(record['value'] ?? ''),
      voteCount: Number(record['voteCount'] ?? record['vote_count'] ?? 0),
      dislikeCount: Number(record['dislikeCount'] ?? record['dislike_count'] ?? 0),
      score: Number(record['score'] ?? 0),
      createdAt: this.optionalString(record['createdAt'] ?? record['created_at']),
      createdByCurrentUser: Boolean(record['createdByCurrentUser'] ?? record['created_by_current_user'] ?? false),
      votedByCurrentUser: Boolean(record['votedByCurrentUser'] ?? record['voted_by_current_user'] ?? false),
      dislikedByCurrentUser: Boolean(record['dislikedByCurrentUser'] ?? record['disliked_by_current_user'] ?? false)
    };
  }

  private mapTopContributor(value: unknown, fallbackRank: number): TopContributor {
    const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};
    const username = this.optionalString(record['username'] ?? record['userName'] ?? record['public_username'] ?? record['publicUsername']);
    const displayName = this.optionalString(record['displayName'] ?? record['display_name'] ?? record['name'] ?? record['fullName'] ?? record['full_name'])
      ?? (username ? `@${username.replace(/^@/, '')}` : 'Anonymous contributor');
    const avatar = this.optionalString(record['avatar'] ?? record['avatarUrl'] ?? record['avatar_url'] ?? record['profilePictureUrl'] ?? record['profile_picture_url']) ?? '';
    return {
      rank: Number(record['rank'] ?? fallbackRank) || fallbackRank,
      username: username?.replace(/^@/, '') ?? '',
      displayName,
      avatar: this.resolveApiLink(avatar),
      proposalCount: Number(record['proposalCount'] ?? record['proposal_count'] ?? 0) || 0,
      voteCount: Number(record['voteCount'] ?? record['vote_count'] ?? 0) || 0,
      reactionCount: Number(record['reactionCount'] ?? record['reaction_count'] ?? 0) || 0,
      reviewCount: Number(record['reviewCount'] ?? record['review_count'] ?? 0) || 0,
      receivedLikeCount: Number(record['receivedLikeCount'] ?? record['received_like_count'] ?? 0) || 0,
      receivedDislikeCount: Number(record['receivedDislikeCount'] ?? record['received_dislike_count'] ?? 0) || 0,
      score: Number(record['score'] ?? 0) || 0,
      isCurrentUser: Boolean(record['isCurrentUser'] ?? record['is_current_user'] ?? false),
      badges: Array.isArray(record['badges']) ? record['badges'].map(badge => {
        const badgeRecord = badge && typeof badge === 'object' ? badge as Record<string, unknown> : {};
        return {
          key: String(badgeRecord['key'] ?? ''),
          label: this.optionalString(badgeRecord['label']),
          labelKey: this.optionalString(badgeRecord['labelKey'] ?? badgeRecord['label_key']),
          imageUrl: this.optionalString(badgeRecord['imageUrl'] ?? badgeRecord['image_url']),
          altKey: this.optionalString(badgeRecord['altKey'] ?? badgeRecord['alt_key'])
        };
      }) : [],
      contributions: Array.isArray(record['contributions']) ? record['contributions'].map(item => this.mapUserContributionSummary(item)) : []
    };
  }

  private mapUserContributionSummary(value: unknown): UserContributionSummary {
    const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};
    return {
      offerId: String(record['offerId'] ?? record['offer_id'] ?? ''),
      field: String(record['field'] ?? ''),
      value: String(record['value'] ?? ''),
      voteCount: Number(record['voteCount'] ?? record['vote_count'] ?? 0) || 0,
      dislikeCount: Number(record['dislikeCount'] ?? record['dislike_count'] ?? 0) || 0,
      score: Number(record['score'] ?? 0) || 0,
      createdAt: this.optionalString(record['createdAt'] ?? record['created_at'])
    };
  }

  private mapOfferSubmission(record: Record<string, unknown>): OfferSubmission {
    return {
      submissionId: String(record['submissionId'] ?? record['submission_id'] ?? ''),
      status: String(record['status'] ?? ''),
      createdAt: String(record['createdAt'] ?? record['created_at'] ?? ''),
      updatedAt: this.optionalString(record['updatedAt'] ?? record['updated_at']),
      reviewedAt: this.optionalString(record['reviewedAt'] ?? record['reviewed_at']),
      reviewedBy: this.optionalString(record['reviewedBy'] ?? record['reviewed_by']),
      moderationNotes: this.optionalString(record['moderationNotes'] ?? record['moderation_notes']),
      contributorEmail: String(record['contributorEmail'] ?? record['contributor_email'] ?? ''),
      title: String(record['title'] ?? ''),
      organization: String(record['organization'] ?? ''),
      country: String(record['country'] ?? ''),
      location: String(record['location'] ?? ''),
      deadline: String(record['deadline'] ?? ''),
      publishedAt: String(record['publishedAt'] ?? record['published_at'] ?? ''),
      sourceUrl: String(record['sourceUrl'] ?? record['source_url'] ?? ''),
      applyLink: String(record['applyLink'] ?? record['apply_link'] ?? ''),
      researchAreas: this.optionalStringArray(record['researchAreas'] ?? record['research_areas']) ?? [],
      description: String(record['description'] ?? ''),
      requirements: this.optionalStringArray(record['requirements']) ?? [],
      benefits: this.optionalStringArray(record['benefits']) ?? [],
      contact: String(record['contact'] ?? ''),
      hasPdf: Boolean(record['hasPdf'] ?? record['has_pdf'] ?? false),
      pdf: record['pdf'] && typeof record['pdf'] === 'object' ? record['pdf'] as OfferSubmission['pdf'] : undefined
    };
  }

  private mapUsageStatus(status: Record<string, unknown>): UsageStatus {
    return {
      userId: this.optionalString(status['user_id'] ?? status['userId']),
      feature: String(status['feature'] ?? ''),
      period: String(status['period'] ?? ''),
      limit: Number(status['limit'] ?? 0),
      used: Number(status['used'] ?? 0),
      reserved: Number(status['reserved'] ?? 0),
      remaining: Number(status['remaining'] ?? 0),
      resetAt: String(status['reset_at'] ?? status['resetAt'] ?? '')
    };
  }

  private mapNotificationPreferences(preferences: Record<string, unknown>): NotificationPreferences {
    return {
      email: Boolean(preferences['email'] ?? true),
      sms: Boolean(preferences['sms'] ?? false),
      whatsapp: Boolean(preferences['whatsapp'] ?? false),
      discord: Boolean(preferences['discord'] ?? false),
      inApp: Boolean(preferences['inApp'] ?? preferences['in_app'] ?? true),
      digest: String(preferences['digest'] ?? 'daily'),
      immediateAlerts: Boolean(preferences['immediateAlerts'] ?? preferences['immediate_alerts'] ?? true),
      marketing: Boolean(preferences['marketing'] ?? false),
      product: Boolean(preferences['product'] ?? true),
      security: Boolean(preferences['security'] ?? true),
      stopProcessing: Boolean(preferences['stopProcessing'] ?? preferences['stop_processing'] ?? false),
      smsCountryCode: this.optionalString(preferences['smsCountryCode'] ?? preferences['sms_country_code']),
      smsPhoneNumber: this.optionalString(preferences['smsPhoneNumber'] ?? preferences['sms_phone_number']),
      smsPhoneNumberVerified: Boolean(preferences['smsPhoneNumberVerified'] ?? preferences['sms_phone_number_verified'] ?? false),
      smsVerificationRequired: Boolean(preferences['smsVerificationRequired'] ?? preferences['sms_verification_required'] ?? false),
      smsVerificationRequestedAt: this.optionalString(preferences['smsVerificationRequestedAt'] ?? preferences['sms_verification_requested_at']),
      whatsappPhoneNumber: this.optionalString(preferences['whatsappPhoneNumber'] ?? preferences['whatsapp_phone_number']),
      discordUsername: this.optionalString(preferences['discordUsername'] ?? preferences['discord_username']),
      discordMessagePrefix: this.optionalString(preferences['discordMessagePrefix'] ?? preferences['discord_message_prefix']),
      notificationMode: this.optionalString(preferences['notificationMode'] ?? preferences['notification_mode']),
      preferencesUpdatedAt: this.optionalString(preferences['preferencesUpdatedAt'] ?? preferences['preferences_updated_at']),
      unsubscribeReason: this.optionalString(preferences['unsubscribeReason'] ?? preferences['unsubscribe_reason']),
      unsubscribedAt: this.optionalString(preferences['unsubscribedAt'] ?? preferences['unsubscribed_at']),
      objectionReason: this.optionalString(preferences['objectionReason'] ?? preferences['objection_reason']),
      objectedAt: this.optionalString(preferences['objectedAt'] ?? preferences['objected_at']),
      objectionRevokedAt: this.optionalString(preferences['objectionRevokedAt'] ?? preferences['objection_revoked_at'])
    };
  }

  private mapOfferStatus(value: unknown): Offer['status'] {
    const rawStatus = String(value ?? 'Seen').trim();
    const status = rawStatus ? `${rawStatus.charAt(0).toUpperCase()}${rawStatus.slice(1).toLowerCase()}` : 'Seen';
    return ['Seen', 'Liked', 'Applied', 'Interview', 'Archived'].includes(status) ? status as Offer['status'] : 'Seen';
  }

  private mapOfferTier(value: unknown): Offer['tier'] {
    const tier = String(value ?? 'C');
    return ['S', 'A', 'B', 'C'].includes(tier) ? tier as Offer['tier'] : 'C';
  }

  private mapOfferDeadline(...values: unknown[]): string {
    for (const value of values) {
      const text = String(value ?? '').trim();
      if (text && !this.isOpenDeadline(text)) return text;
    }
    return 'unknown';
  }

  private isOpenDeadline(value: string): boolean {
    const normalized = value.toLowerCase();
    return !normalized
      || normalized === 'unknown'
      || normalized === 'open deadline'
      || normalized === 'rolling'
      || normalized.startsWith(ApiService.OPEN_DEADLINE_SENTINEL);
  }

  private optionalString(value: unknown): string | undefined {
    const text = String(value ?? '').trim();
    return text ? text : undefined;
  }

  private mapOfferText(values: unknown[], fallback: string): string {
    for (const value of values) {
      const text = String(value ?? '').trim();
      if (text && !this.isPlaceholderOfferText(text)) return text;
    }
    return fallback;
  }

  private isPlaceholderOfferText(value: string): boolean {
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    return [
      '',
      'unknown',
      'unknown organization',
      'unknown location',
      'unspecified',
      'n a',
      'none',
      'null',
      'not provided',
      'not specified',
      'research institution'
    ].includes(normalized);
  }

  private optionalNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private optionalBoolean(value: unknown): boolean | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
    return Boolean(value);
  }

  private unknownString(value: unknown): string {
    return this.optionalString(value) ?? 'unknown';
  }

  private optionalStringArray(value: unknown): string[] | undefined {
    if (!Array.isArray(value)) return undefined;
    const values = value.map(item => String(item).trim()).filter(Boolean);
    return values.length ? values : undefined;
  }

  private mapScoreBreakdown(value: unknown): Offer['scoreBreakdown'] {
    if (!Array.isArray(value)) return undefined;
    return value.map(item => {
      const record = item && typeof item === 'object' ? item as Record<string, unknown> : {};
      return {
        label: String(record['label'] ?? ''),
        value: Number(record['value'] ?? 0),
        rationale: String(record['rationale'] ?? '')
      };
    });
  }

  private formatUpdatedAt(value: string): string {
    return value.includes('.') ? value.replace(/\.\d+/, '') : value;
  }
}
